import { ApiRoutes, Mensagens, MotivoContato } from "../../models/types";
import { z } from "zod";
import { useAxiosClient } from "../../hooks/useAxiosClient";
import { useIonLoading } from "@ionic/react";
import { useContaUsuario } from "../../contexts/contaUsuarioContext";
import { useProtocolo } from "../../contexts/protocoloContext";
import { getMatriculaSolicitante } from "../../utils/functions";
import useNotification from "../../hooks/useNotification";
import Accordion from "../../components/Accordion";
import DivEmailIndividualizados from "../../components/DivEmailIndividualizados";
import Button from "../../components/Button";
import Page from "../../components/Page";
import ReactForm from "../../components/form/ReactForm";
import InputText from "../../components/form/InputText";
import SubmitButton from "../../components/form/SubmitButton";
import ButtonBack from "../../components/ButtonBack";

export default function AlteracaoEmailIndividualizados() {
  const contaUsuarioContext = useContaUsuario();
  const notification = useNotification();
  const axiosAtendimento = useAxiosClient(ApiRoutes.ECO_ATENDIMENTO);

  const { getProtocolo } = useProtocolo();
  const [present, dismiss] = useIonLoading();

  const formSchema = z
    .object({
      emailAtual: z.string(),
      email: z.string().email({ message: "Informe um e-mail válido." }),
      confirmarEmail: z
        .string()
        .email({ message: "Confirmação de e-mail inválida." }),
    })
    .refine((data) => data.email === data.confirmarEmail, {
      message: "Os dois e-mails informados devem ser iguais.",
      path: ["confirmarEmail"],
    })
    .refine((data) => data.email !== data.emailAtual, {
      message: "O novo e-mail não pode ser igual ao e-mail atual.",
      path: ["email"],
    });

  type formType = z.infer<typeof formSchema>;

  async function enviarDados(dados: formType, resetFields: any) {
    await present({ message: Mensagens.SALVANDO });

    const protocolo = await getProtocolo({
      numeroConta: contaUsuarioContext?.contaSelecionada?.numeroConta || null,
      dddTelefoneContato: null,
      numeroTelefoneContato: null,
      codigoMotivoContato: MotivoContato.ALTERACAO_ENVIO_AUTOMATICO_FATURAMENTO_INDIVIDUALIZADO,
    });

    if (!protocolo || !protocolo.numeroProtocolo) {
      dismiss();
      return;
    }

    try {
      await axiosAtendimento.patch(
        "/atendimentos/resumo-faturamento-individualizado",
        JSON.stringify({
          email: dados.email,
          matriculaSolicitante: getMatriculaSolicitante(),
          numeroConta:
            contaUsuarioContext?.contaSelecionada?.numeroContaComDigito,
          protocolo: protocolo.numeroProtocolo,
        }),
        {
          headers: {
            "content-type": "application/json",
          },
        }
      );

      notification.showProtocolo(protocolo.numeroProtocolo).then(() => {
        contaUsuarioContext.reconsultarDadosContaSelecionada();
        resetFields();
      });

      dismiss();
    } catch (e: any) {
      dismiss();
      await notification.showError({ exception: e });
    }
  }

  async function cancelarEnvioAutomatico() {
    await present({ message: Mensagens.SALVANDO });

    const protocolo = await getProtocolo({
      numeroConta: contaUsuarioContext?.contaSelecionada?.numeroConta || null,
      dddTelefoneContato: null,
      numeroTelefoneContato: null,
      codigoMotivoContato: MotivoContato.CANCELAMENTO_ENVIO_AUTOMATICO_FATURAMENTO_INDIVIDUALIZADO, 
    });

    if (!protocolo || !protocolo.numeroProtocolo) {
      dismiss();
      return;
    }

    try {
      await axiosAtendimento.put(
        "/atendimentos/resumo-faturamento-individualizado",
        JSON.stringify({
          matriculaSolicitante: getMatriculaSolicitante(),
          numeroConta:
            contaUsuarioContext?.contaSelecionada?.numeroContaComDigito,
          protocolo: protocolo.numeroProtocolo,
        }),
        {
          headers: {
            "content-type": "application/json",
          },
        }
      );

      notification.showProtocolo(protocolo.numeroProtocolo).then(() => {
        contaUsuarioContext.reconsultarDadosContaSelecionada();
      });

      dismiss();
    } catch (e: any) {
      dismiss();

      await notification.showError({ exception: e });
    }
  }

  return (
    <Page title="Consumo e faturamento individualizados">
      <div className="flex flex-col space-y-4">
        <Accordion title="Observações" open>
          <DivEmailIndividualizados />
        </Accordion>

        <div className="card bg-opacity-70">
          <div className="card-title">Alteração de e-mail automático</div>

          <div className="items-center">
            <ReactForm
              schema={formSchema}
              onError={(error, e) => {}}
              onSubmit={(data, resetFields) =>
                enviarDados(data as formType, resetFields)
              }
            >
              <div className="flex flex-col space-y-4">
                <InputText
                  name="emailAtual"
                  label="E-mail atual"
                  value={
                    contaUsuarioContext.contaSelecionada
                      ?.emailResumoFaturamentoIndividualizado
                  }
                  readonly
                />
                <InputText
                  name="email"
                  label="Novo e-mail"
                  inputProps={{ maxLength: 60 }}
                />
                <InputText
                  name="confirmarEmail"
                  label="Confirmar e-mail"
                  inputProps={{ maxLength: 60 }}
                  onPaste={(e) => e.preventDefault()}
                />

                <div className="grid grid-cols-3 gap-2 xs:grid-cols-1">
                  <SubmitButton label="Alterar" />
                  <Button
                    label="Descadastrar"
                    onClick={() => cancelarEnvioAutomatico()}
                  />
                  <ButtonBack label="Voltar" />
                </div>
              </div>
            </ReactForm>
          </div>
        </div>
      </div>
    </Page>
  );
}
